import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import {
  LayoutDashboard,
  GitBranch,
  Calendar,
  Brain,
  Users,
  Phone,
  MessageCircle,
  Mail,
  Linkedin,
  Target,
  Megaphone,
  GraduationCap,
  ChevronDown,
  ChevronRight,
  UserCheck,
  UserPlus
} from 'lucide-react';

interface SidebarProps {
  isExpanded: boolean;
  setIsExpanded: (expanded: boolean) => void;
}

interface NavItemProps {
  to?: string;
  icon: React.ReactNode;
  label: string;
  active: boolean;
  isExpanded: boolean;
  onClick?: () => void;
  isButton?: boolean;
  chevron?: React.ReactNode;
  disabled?: boolean;
  isOpen?: boolean;
}

const NavItem: React.FC<NavItemProps> = ({
  to,
  icon,
  label,
  active,
  isExpanded,
  onClick,
  isButton,
  chevron,
  disabled,
  isOpen,
}) => {
  const baseClasses = `group relative flex items-center py-2.5 mx-2 rounded-xl transition-all duration-200 ease-in-out px-3 ${
    active
      ? 'sidebar-active-item bg-gradient-to-r from-blue-50 to-blue-50/30 text-blue-700'
      : isOpen
        ? 'bg-gray-100/60 text-gray-700'
        : 'text-gray-500 hover:bg-gray-100/70 hover:text-gray-800'
  }`;

  const iconContainer = (
    <div
      className={`w-8 h-8 flex items-center justify-center flex-shrink-0 rounded-lg transition-all duration-200 ${
        active
          ? 'bg-blue-100/80 text-blue-600 shadow-sm'
          : isOpen
            ? 'bg-blue-50/60 text-gray-600'
            : 'bg-gray-100/60 text-gray-400 group-hover:bg-blue-50 group-hover:text-gray-600'
      }`}
    >
      {icon}
    </div>
  );

  const labelEl = (
    <span
      className={`font-medium ml-3 transition-all duration-300 ease-in-out whitespace-nowrap ${
        isExpanded ? 'opacity-100 w-auto' : 'opacity-0 w-0 overflow-hidden'
      } ${active ? 'font-semibold' : ''}`}
    >
      {label}
    </span>
  );

  const content = (
    <>
      {iconContainer}
      {labelEl}
      {chevron && (
        <span
          className={`ml-auto transition-all duration-300 ease-in-out ${
            isExpanded ? 'opacity-100 w-auto' : 'opacity-0 w-0 overflow-hidden'
          }`}
        >
          {chevron}
        </span>
      )}
    </>
  );

  if (isButton) {
    return (
      <button
        onClick={onClick}
        className={`w-full ${baseClasses}`}
        disabled={disabled}
      >
        {content}
      </button>
    );
  }

  return (
    <Link to={to!} className={baseClasses}>
      {content}
    </Link>
  );
};

interface SubNavItemProps {
  to: string;
  icon: React.ReactNode;
  label: string;
  active: boolean;
}

const SubNavItem: React.FC<SubNavItemProps> = ({ to, icon, label, active }) => (
  <Link
    to={to}
    className={`group relative flex items-center py-2 rounded-lg text-sm transition-all duration-200 pl-2 ${
      active
        ? 'text-blue-700 font-medium'
        : 'text-gray-600 hover:text-gray-800 hover:bg-gray-100/60'
    }`}
  >
    <div
      className={`w-6 h-6 flex items-center justify-center flex-shrink-0 rounded-md transition-all duration-200 ${
        active
          ? 'bg-blue-100/60 text-blue-600'
          : 'text-gray-500 group-hover:text-gray-600'
      }`}
    >
      {icon}
    </div>
    <span className="ml-2.5 whitespace-nowrap">{label}</span>
  </Link>
);

const SectionDivider: React.FC = () => (
  <div className="mx-4 my-2">
    <div className="h-px bg-gradient-to-r from-transparent via-gray-200 to-transparent" />
  </div>
);

const Sidebar: React.FC<SidebarProps> = ({ isExpanded, setIsExpanded }) => {
  const location = useLocation();
  const [expandedMenus, setExpandedMenus] = useState<string[]>([]);

  const toggleMenu = (menuId: string) => {
    setExpandedMenus(prev =>
      prev.includes(menuId)
        ? prev.filter(id => id !== menuId)
        : [...prev, menuId]
    );
  };

  const isActive = (path: string) => location.pathname === path;
  const isParentActive = (paths: string[]) =>
    paths.some(path => location.pathname === path);

  const aiAgentPaths = [
    '/ai-agents/lead-gen',
    '/ai-agents/call-agent',
    '/ai-agents/whatsapp-agent',
    '/ai-agents/email-agent',
    '/ai-agents/linkedin-agent',
  ];

  const leadPaths = ['/leads/outbound', '/leads/inbound'];

  return (
    <div
      className={`${
        isExpanded ? 'w-64' : 'w-16'
      } bg-gray-50/80 h-full overflow-y-auto overflow-x-hidden transition-all duration-300 ease-in-out fixed left-0 top-0 z-10 sidebar-scrollbar`}
      style={{
        boxShadow: '2px 0 12px -3px rgba(0, 0, 0, 0.06), 1px 0 4px -2px rgba(0, 0, 0, 0.03)',
      }}
      onMouseEnter={() => setIsExpanded(true)}
      onMouseLeave={() => setIsExpanded(false)}
    >
      <div
        className={`flex items-center py-6 transition-all duration-300 ease-in-out ${
          isExpanded ? 'px-5' : 'px-4'
        }`}
      >
        <div className="flex items-center">
          <div className="w-8 h-8 flex items-center justify-center flex-shrink-0">
            <img src="/salespad logo.svg" alt="SalesPad" className="w-8 h-8" />
          </div>
          <span
            className={`text-xl font-extrabold ml-3 font-display bg-gradient-to-r from-[#1875FF] via-[#1875FF] via-25% to-[#05E0D1] bg-clip-text text-transparent transition-all duration-300 ease-in-out whitespace-nowrap ${
              isExpanded ? 'opacity-100 w-auto' : 'opacity-0 w-0 overflow-hidden'
            }`}
          >
            <span className="font-display">SalesPad</span>
          </span>
        </div>
      </div>

      <div className="mx-4 mb-3">
        <div className="h-px bg-gradient-to-r from-transparent via-blue-200/50 to-transparent" />
      </div>

      <nav className="pb-4 space-y-0.5">
        <NavItem
          to="/"
          icon={<LayoutDashboard size={20} />}
          label="Dashboard"
          active={isActive('/')}
          isExpanded={isExpanded}
        />
        <NavItem
          to="/pipeline"
          icon={<GitBranch size={20} />}
          label="Pipeline"
          active={isActive('/pipeline')}
          isExpanded={isExpanded}
        />
        <NavItem
          to="/meetings"
          icon={<Calendar size={20} />}
          label="Meetings"
          active={isActive('/meetings')}
          isExpanded={isExpanded}
        />

        <SectionDivider />

        <div>
          <NavItem
            icon={<Brain size={20} />}
            label="AI Agents"
            active={isParentActive(aiAgentPaths)}
            isExpanded={isExpanded}
            isButton
            isOpen={expandedMenus.includes('ai-agents')}
            onClick={() => toggleMenu('ai-agents')}
            disabled={!isExpanded}
            chevron={
              expandedMenus.includes('ai-agents') ? (
                <ChevronDown size={16} />
              ) : (
                <ChevronRight size={16} />
              )
            }
          />

          {isExpanded && expandedMenus.includes('ai-agents') && (
            <div className="relative ml-7 mr-2 mt-1 space-y-0.5 sidebar-submenu">
              <SubNavItem
                to="/ai-agents/lead-gen"
                icon={<Users size={15} />}
                label="Lead Gen Agent"
                active={isActive('/ai-agents/lead-gen')}
              />
              <SubNavItem
                to="/ai-agents/call-agent"
                icon={<Phone size={15} />}
                label="Call Agent"
                active={isActive('/ai-agents/call-agent')}
              />
              <SubNavItem
                to="/ai-agents/whatsapp-agent"
                icon={<MessageCircle size={15} />}
                label="WhatsApp Agent"
                active={isActive('/ai-agents/whatsapp-agent')}
              />
              <SubNavItem
                to="/ai-agents/email-agent"
                icon={<Mail size={15} />}
                label="Email Agent"
                active={isActive('/ai-agents/email-agent')}
              />
              <SubNavItem
                to="/ai-agents/linkedin-agent"
                icon={<Linkedin size={15} />}
                label="LinkedIn Agent"
                active={isActive('/ai-agents/linkedin-agent')}
              />
            </div>
          )}
        </div>

        <div>
          <NavItem
            icon={<Target size={20} />}
            label="Leads"
            active={isParentActive(leadPaths)}
            isExpanded={isExpanded}
            isButton
            isOpen={expandedMenus.includes('leads')}
            onClick={() => toggleMenu('leads')}
            disabled={!isExpanded}
            chevron={
              expandedMenus.includes('leads') ? (
                <ChevronDown size={16} />
              ) : (
                <ChevronRight size={16} />
              )
            }
          />

          {isExpanded && expandedMenus.includes('leads') && (
            <div className="relative ml-7 mr-2 mt-1 space-y-0.5 sidebar-submenu">
              <SubNavItem
                to="/leads/outbound"
                icon={<UserCheck size={15} />}
                label="Outbound"
                active={isActive('/leads/outbound')}
              />
              <SubNavItem
                to="/leads/inbound"
                icon={<UserPlus size={15} />}
                label="Inbound"
                active={isActive('/leads/inbound')}
              />
            </div>
          )}
        </div>

        <SectionDivider />

        <NavItem
          to="/campaigns"
          icon={<Megaphone size={20} />}
          label="Campaigns"
          active={isActive('/campaigns')}
          isExpanded={isExpanded}
        />
        <NavItem
          to="/sales-coach"
          icon={<GraduationCap size={20} />}
          label="Sales Coach"
          active={isActive('/sales-coach')}
          isExpanded={isExpanded}
        />
      </nav>
    </div>
  );
};

export default Sidebar;
